'use client';

import React, { useMemo } from 'react';
import { Box, ButtonProps } from '@mui/material';

import { ButtonLabel, Row } from '@dh/styles';
import { useTheme } from '@dh/hooks';
import { keyboardSelect } from '@dh/utils/keyboardSelect';

import { BigSideNavItemButton } from './styles';

interface Props extends ButtonProps {
    icon?: React.JSX.Element;
    selectedIcon?: React.JSX.Element;
    title: string;
    isSelected: boolean;
    handleOnClick: () => void;
}

export const BigSidenavItem = React.memo<Props>(({
    icon,
    selectedIcon,
    title,
    isSelected,
    handleOnClick,
    ...buttonProps
}: Props) => {
    const theme = useTheme();
    const hasIcon = icon ?? (isSelected && selectedIcon);

    // Memoize the icon to display
    const displayedIcon = useMemo(
        () => (isSelected && selectedIcon ? selectedIcon : (icon ?? null)),
        [isSelected, selectedIcon, icon]
    );

    // Memoize color calculation
    const iconColor = useMemo(
        () => (isSelected ? theme.colors.onPrimaryDark : theme.colors.surfaceVariant),
        [isSelected, theme.colors.onPrimaryDark, theme.colors.surfaceVariant]
    );

    return (
        <Box
            onKeyDown={(event) => {
                keyboardSelect(event, handleOnClick);
            }}
            tabIndex={0}
            aria-label={`Navigation-Item-${title}`}
        >
            <BigSideNavItemButton
                isSelected={isSelected}
                onClick={handleOnClick}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                {...buttonProps}
                tabIndex={-1}
            >
                <Row
                    mr={3}
                    ml={hasIcon ? 1 : 3}
                    color={iconColor}
                >
                    {displayedIcon}
                    <ButtonLabel ml={hasIcon ? 1 : 0}>{title}</ButtonLabel>
                </Row>
            </BigSideNavItemButton>
        </Box>
    );
});

BigSidenavItem.displayName = 'BigSidenavItem';
